import React from 'react'
import { useTranslation } from 'react-i18next'
import { SITE_NAME } from '../../../../constants'

interface IProps {
  variant?: {
    weightOverride: number
    lengthOverride: number
    shippingRequired: boolean
    widthOverride: number
    heightOverride: number
    isSupply: boolean
    sku: string
    upc: string
    ean: string
    isbn: string
  }
  product?: {
    showVariantWeight: boolean
    isShippingRequired: boolean
    isWeightVisible: boolean
    isLengthVisible: boolean
    isEanVisible: boolean
    isIsbnVisible: boolean
    isUpcVisible: boolean
    showVariantEan: boolean
    showVariantUpc: boolean
    showVariantIsbn: boolean

    weight: number
    length: number
    width: number
    height: number
    showVariantLength: boolean
    isSkuVisible: boolean
    sku: string
    upc: string
    ean: string
    isbn: string
    showVariantSku: boolean
    manufacturer: { name: string } | null
  }
  weight_k: number
  length_k: number
}

const VariantProperties: React.FC<IProps> = ({ variant, product, weight_k, length_k }) => {
  const { t } = useTranslation()
  if (!product) return null

  const initWeightAndDimensions = () => {
    let weight
    if (product.showVariantWeight && variant) {
      weight = variant?.weightOverride ? variant?.weightOverride / weight_k : null
    } else if (variant) {
      weight = product?.weight && variant.shippingRequired ? product?.weight / weight_k : null
    } else {
      weight = t('dependsOnVariation', 'Depends on variation')
    }
    const weightText = weight ? t('weight(kg)', 'WEIGHT(KG)') + ': ' + weight : ''

    let dimensions
    if (product.showVariantLength && variant) {
      dimensions = [variant.lengthOverride || 0, variant.widthOverride || 0, variant.heightOverride || 0]
    } else if (variant) {
      dimensions = [product.length || 0, product.width || 0, product.height || 0]
    } else {
      dimensions = t('dependsOnVariation', 'Depends on variation')
    }
    let dimensionsText
    if (dimensions === t('dependsOnVariation', 'Depends on variation')) {
      dimensionsText = t('dimensionsMmDependsOnVariation', 'DIMENSIONS(MM): Depends on variation')
    } else {
      dimensionsText = t('productDimensionsMm', {
        defaultValue: 'DIMENSIONS(MM): L: {{length}}; W: {{width}}; H: {{height}};',
        length: dimensions[0] / length_k,
        width: dimensions[1] / length_k,
        height: dimensions[2] / length_k,
      })
    }

    return variant?.shippingRequired || (!variant && product.isShippingRequired)
      ? [weightText, dimensionsText]
      : ['', '']
  }
  const text = initWeightAndDimensions()
  const weight =
    text[0] && !variant?.isSupply ? (
      <div className='variant-info__weight'>
        <span className='list__name'>{text[0]}</span>
      </div>
    ) : (
      ''
    )
  const dimensions =
    text[1] && !variant?.isSupply ? (
      <div className='product-info__size'>
        <span className='list__name'>{text[1]}</span>
      </div>
    ) : (
      ''
    )
  return (
    <div className='text-base text-gray leading-loose'>
      <div>
        {variant && (
          <>
            <span className='uppercase'>{t('type', 'Type')}</span>:{' '}
            {variant.shippingRequired ? t('typePhysical', 'Physical') : t('typeDigital', 'Digital')}
          </>
        )}
        {!variant && (
          <>
            <span className='uppercase'>{t('type', 'Type')}</span>:{' '}
            {product.isShippingRequired ? t('typePhysical', 'Physical') : t('typeDigital', 'Digital')}
          </>
        )}
      </div>
      {product.isSkuVisible && (
        <>
          {!product.showVariantSku && product.sku && (
            <div>
              <span className='uppercase'>{t('sku', 'SKU')}</span>: {product.sku}
            </div>
          )}
          {product.showVariantSku && variant?.sku && (
            <div>
              <span className='uppercase'>{t('sku', 'SKU')}</span>: {variant.sku}
            </div>
          )}
        </>
      )}
      <div>
        <span className='uppercase'>{t('brand', 'Brand')}: </span>
        <span itemProp='brand'>{product.manufacturer?.name || SITE_NAME}</span>
      </div>
      {product.isWeightVisible && weight}
      {product.isLengthVisible && dimensions}
      {product.isEanVisible && (
        <>
          {!product.showVariantEan && product.ean && (
            <div>
              <span className='uppercase'>{t('ean', 'EAN')}</span>: {product.ean}
            </div>
          )}
          {product.showVariantEan && variant?.ean && (
            <div>
              <span className='uppercase'>{t('ean', 'EAN')}</span>: {variant.ean}
            </div>
          )}
        </>
      )}
      {product.isUpcVisible && (
        <>
          {!product.showVariantUpc && product.upc && (
            <div>
              {t('upc', 'UPC')}: {product.upc}
            </div>
          )}
          {product.showVariantUpc && variant?.upc && (
            <div>
              {t('upc', 'UPC')}: {variant.upc}
            </div>
          )}
        </>
      )}
      {product.isIsbnVisible && (
        <>
          {!product.showVariantIsbn && product.isbn && (
            <div>
              {t('isbn', 'ISBN')}: {product.isbn}
            </div>
          )}
          {product.showVariantIsbn && variant?.isbn && (
            <div>
              {t('isbn', 'ISBN')}: {variant.isbn}
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default React.memo(VariantProperties)
